import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { usePOItemsFiltersActions } from "../../actions/po-items-filters-actions";
import { useStore } from "../../store";
import { useQuery } from "../../utils";
import {
  deleteRedirectionUrl,
  getPersistedRedirectionUrl,
  persistRedirectionUrl
} from "../../utils/local-storage/redirection";
import { useApplicationSettingsHelpers } from "../settings/application-settings-helpers";

/**
 * The Redirection view for redirecting based on URL query parameters.
 */
const RedirectionView = () => {
  const [state] = useStore();
  const [currentUrlQuery] = useState<URLSearchParams>(useQuery());
  const [redirectionKey, setRedirectionKey] = useState<string>("");
  const navigate = useNavigate();
  const { setItemsRedirectionFilters } = usePOItemsFiltersActions();
  const { handleCustomerChanged } = useApplicationSettingsHelpers();
  const loading = state.fetchingData.user;

  useEffect(() => {
    if (currentUrlQuery.size === 0) return;
    const queryKeyValues: [string, string][] = [];
    currentUrlQuery.entries().forEach(keyValues => queryKeyValues.push(keyValues));

    handleUrlQuery(queryKeyValues[0][0], queryKeyValues[0][1]); // Check the first query parameter.
  }, [state.fetchingData.user]);

  const handleUrlQuery = async (queryParamsKey: string, queryParamsValue: string) => {
    switch (queryParamsKey) {
      case "sold-to-party":
        redirectBySoldToParty(queryParamsValue);
        break;
      case "sales-order":
        setRedirectionKey("salesOrderNumber");
        redirectBySalesOrder(queryParamsValue, currentUrlQuery.get("sold-to-party") ?? "");
        break;
      default:
        return;
    }
  };

  /**
   * Navigates to /dashboard if the soldToPartyNumber matches the current customer.
   * Otherwise, if soldToPartyNumber matches any other assigned customer, switches to
   * that customer and navigates to /dashboard.
   * @param soldToPartyNumber
   */
  const redirectBySoldToParty = (soldToPartyNumber: string) => {
    const matchedCustomer = state.user?.customers.find(
      c => c.soldToPartyNumber === soldToPartyNumber
    );

    if (matchedCustomer) {
      if (state.customer?.soldToPartyNumber === soldToPartyNumber) {
        navigate("/dashboard");
      } else {
        if (!loading) handleCustomerChanged(matchedCustomer.id);
      }
    }
  };

  /**
   * Navigates to /purchase-order-items if the salesOrderNumber belongs to the
   * current customer. Otherwise, if the salesOrderNumber belongs to any other
   * assigned customer, switches to that customer and navigates to /purchase-order-items.
   * @param salesOrderNumber
   */
  const redirectBySalesOrder = (salesOrderNumber: string, soldToPartyNumber: string) => {
    if (!currentUrlQuery || !state.customer || soldToPartyNumber.length === 0) return;

    const matchedCustomer = state.user?.customers.find(
      c => c.soldToPartyNumber === soldToPartyNumber
    );
    const hasStoredRedirection = getPersistedRedirectionUrl().queryString ? true : false;

    if (matchedCustomer !== undefined) {
      // In case of the Sales Order belonging to the current customer.
      if (matchedCustomer.id === state.customer.id) {
        if (hasStoredRedirection) deleteRedirectionUrl();

        setItemsRedirectionFilters({
          salesOrderLine: [salesOrderNumber]
        });
        navigate("/purchase-order-items");
      }
      // In case of the Sales Order belonging to another assigned customer.
      else {
        persistRedirectionUrl({
          queryString: currentUrlQuery.toString()
        });
        if (!loading) handleCustomerChanged(matchedCustomer.id);
      }
    }
    // In all other cases, redirect to /dashboard.
    else navigate("/dashboard");
  };

  return (
    <main className="redirect-container">
      <div className="signin redirect-logo">
        <div className="watermark">
          <i className="icon icon-econ" />
          <div className="product">Ericsson Customer Order Dashboard</div>
        </div>
      </div>
      <div className="redirect-progress">
        <div className="redirect-text">Redirecting...</div>
        <a href="/">Go to ECOD login page</a>
      </div>
      <div className="redirect-placeholder"></div>
    </main>
  );
};

export default RedirectionView;
